import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Badge, Button } from '../components/design-system';
import {
  Bell, BellOff, Check, CheckCheck, Package, Truck, Star,
  MessageSquare, Navigation, ArrowLeft,
} from 'lucide-react';
import courierApi from '../services/courierApi';

const typeIcons = {
  new_match: Package,
  offer_received: MessageSquare,
  offer_accepted: Check,
  offer_rejected: BellOff,
  contract_created: Truck,
  provider_enroute: Navigation,
  provider_arrived: Navigation,
  review_received: Star,
  review_reply: MessageSquare,
};

export const NotificationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const res = await courierApi.getNotifications();
      setNotifications(res.notifications || []);
      setUnreadCount(res.unreadCount || 0);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (id) => {
    try {
      await courierApi.markNotificationRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await courierApi.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) markRead(notification.id);

    const data = notification.data;
    if (data?.contractId) {
      navigate(`/contracts/${data.contractId}`);
    } else if (data?.requestId) {
      navigate('/marketplace');
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  return (
    <div className="p-4 pb-2">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="p-1">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h1 className="text-lg font-bold text-gray-900">{t('notification.title')}</h1>
          {unreadCount > 0 && (
            <Badge variant="danger" size="sm">{unreadCount}</Badge>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="text-xs text-primary-600 font-medium flex items-center gap-1"
          >
            <CheckCheck className="h-3.5 w-3.5" />
            {t('notification.markAllRead')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-3 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-12">
          <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">{t('notification.noNotifications')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((notification) => {
            const Icon = typeIcons[notification.type] || Bell;
            return (
              <button
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`w-full text-left p-3 rounded-xl border transition-colors ${
                  notification.isRead
                    ? 'bg-white border-gray-200'
                    : 'bg-primary-50 border-primary-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    notification.isRead ? 'bg-gray-100' : 'bg-primary-100'
                  }`}>
                    <Icon className={`h-4 w-4 ${notification.isRead ? 'text-gray-500' : 'text-primary-600'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${notification.isRead ? 'text-gray-700' : 'text-gray-900 font-medium'}`}>
                      {notification.title}
                    </p>
                    {notification.body && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notification.body}</p>
                    )}
                  </div>
                  <span className="text-[10px] text-gray-400 flex-shrink-0">
                    {timeAgo(notification.createdAt)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
